import React, { useState } from 'react';
import { Box, Button, Checkbox, Chip, Collapse, Typography } from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import FreeBreakfastIcon from '@mui/icons-material/FreeBreakfast';
import { useTranslation } from 'react-i18next';
import { useLessonPacing } from '../../../shared/classroom-ui/useLessonPacing.js';
import { formatLessonTime } from '../../../shared/classroom-ui/lesson-pacing.js';

export default function StudentPacingNotice({ roomId }) {
    const { t } = useTranslation();
    const { snapshot, view } = useLessonPacing(roomId);
    const [agendaExpanded, setAgendaExpanded] = useState(false);
    const showTimer = Boolean(view?.visibleToStudents);
    const agendaItems = snapshot?.agenda_visible_to_students ? snapshot.agenda_items || [] : [];
    const showAgenda = agendaItems.length > 0;
    if (!showTimer && !showAgenda) return null;

    const onBreak = Boolean(view?.isOnBreak);
    return (
        <Box data-testid="student-lesson-pacing" role="status" aria-live="polite"
            sx={{ display: 'grid', justifyItems: 'center', gap: 0.5, flexShrink: 0, px: 1, py: 0.5 }}>
            {showTimer && <Chip
                size="small"
                icon={onBreak ? <FreeBreakfastIcon /> : <AccessTimeIcon />}
                color={onBreak ? 'info' : view.remainingSeconds <= 300 ? 'warning' : 'default'}
                label={onBreak
                    ? `${t('onLessonBreak', { defaultValue: 'Lesson break' })} · ${formatLessonTime(view.remainingSeconds)}`
                    : `${t('remaining', { defaultValue: 'Remaining' })}: ${formatLessonTime(view.remainingSeconds)}`}
                sx={{ fontFamily: 'monospace', fontWeight: 600 }}
            />}
            {showAgenda && <>
                <Button size="small" aria-expanded={agendaExpanded}
                    aria-controls="student-lesson-agenda"
                    onClick={() => setAgendaExpanded(expanded => !expanded)}>
                    {t('lessonAgenda', { defaultValue: 'Lesson agenda' })} ({agendaItems.filter(item => item.done).length}/{agendaItems.length})
                </Button>
                <Collapse in={agendaExpanded} timeout="auto" unmountOnExit>
                    <Box id="student-lesson-agenda" role="list" sx={{ display: 'grid', maxHeight: 120, overflowY: 'auto', px: 1 }}>
                        {agendaItems.map(item => (
                            <Box key={item.id} role="listitem" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Checkbox size="small" checked={item.done} disabled inputProps={{ 'aria-label': item.text }} />
                                <Typography variant="caption" sx={{ overflowWrap: 'anywhere', textDecoration: item.done ? 'line-through' : 'none' }}>
                                    {item.text}
                                </Typography>
                            </Box>
                        ))}
                    </Box>
                </Collapse>
            </>}
        </Box>
    );
}
